/**
 * BaseLayerProvider
 * 
 * An abstract base class that implements the LayerProvider interface
 * with sensible defaults and helper methods.
 */

import mapboxgl from 'mapbox-gl';
import {
  LayerProvider,
  LayerDataConfig,
  LayerProcessingConfig,
  LayerRenderingConfig,
  LayerOptions,
  LayerHelpers,
  LayerSource
} from './interfaces';

/**
 * Abstract base class for layer providers
 */
export abstract class BaseLayerProvider implements LayerProvider {
  /**
   * Layer data configuration
   */
  dataConfig: LayerDataConfig;
  
  /**
   * Layer processing configuration
   */
  processingConfig: LayerProcessingConfig;
  
  /**
   * Layer rendering configuration
   */
  renderingConfig: LayerRenderingConfig;
  
  /**
   * Layer options
   */
  options: LayerOptions;

  /**
   * Ask for an immediate re-fetch, outside the autoRefresh interval.
   *
   * A provider owns its source configuration but not the fetching, so one that
   * changes its own request — a different query string, say — would otherwise
   * have to wait out the refresh interval before the change was visible. The
   * LiveLayer that drives this provider supplies the requester; until it does,
   * asking is a no-op rather than an error, because a provider may be
   * constructed long before it is put on a map.
   */
  private refreshRequester: (() => void) | null = null;

  setRefreshRequester(requester: () => void): void {
    this.refreshRequester = requester;
  }

  protected requestRefresh(): void {
    this.refreshRequester?.();
  }

  /**
   * Constructor for BaseLayerProvider
   * 
   * @param dataConfig Layer data configuration
   * @param processingConfig Layer processing configuration
   * @param renderingConfig Layer rendering configuration
   * @param options Layer options
   */
  constructor(
    dataConfig: LayerDataConfig,
    processingConfig: LayerProcessingConfig,
    renderingConfig: LayerRenderingConfig,
    options: LayerOptions
  ) {
    this.dataConfig = dataConfig;
    this.processingConfig = processingConfig;
    this.renderingConfig = renderingConfig;
    this.options = this.getDefaultOptions(options);
  }
  
  /**
   * Get default options merged with provided options
   * 
   * @param options User-provided options
   * @returns Merged options
   */
  private getDefaultOptions(options: LayerOptions): LayerOptions {
    return {
      autoRefresh: false,
      refreshInterval: 300000, // 5 minutes
      controlPosition: 'top-left',
      ...options
    };
  }
  
  /**
   * Create a LayerHelpers object to provide to layer functions
   * 
   * @returns LayerHelpers object
   */
  createLayerHelpers(): LayerHelpers {
    return {
      getDisplayName: (propertyName: string): string => {
        if (this.dataConfig.propertyNameMapping && 
            this.dataConfig.propertyNameMapping[propertyName] !== undefined) {
          return this.dataConfig.propertyNameMapping[propertyName];
        }
        return propertyName;
      },
      formatValue: (value: any, propertyName: string): string => {
        if (value === undefined || value === null) {
          return 'N/A';
        }
        
        // Add specific formatting based on property names if needed
        switch(propertyName) {
          case 'temp':
            return `${value}°C`;
          case 'rh':
            return `${value}%`;
          case 'wspd':
          case 'gust':
            return `${value} km/h`;
          case 'rain':
            return `${value} mm`;
          default:
            return value.toString();
        }
      },
      getOption: (optionName: string): any => {
        return this.options[optionName];
      }
    };
  }
  
  /**
   * Build a URL with environment variables if needed
   *
   * @param url URL string possibly containing environment variables
   * @returns Processed URL
   */
  protected processUrl(url: string): string {
    // Replace environment variables in URL if needed
    // Note: With runtime config migration, this method may need updating
    // if config values need to be substituted at runtime
    return url.replace(/\${([^}]+)}/g, (_, varName) => {
      // For runtime config, we no longer use import.meta.env
      // This method should be updated to use configService if needed
      return '';
    });
  }
  
  /**
   * Create source configuration for the layer
   * 
   * @param sourceName The name of the source
   * @returns LayerSource configuration
   */
  protected getSourceConfig(sourceName: string): LayerSource | null {
    const sourceUrl = this.dataConfig.sources[sourceName]?.url;
    if (!sourceUrl) return null;
    
    return {
      url: this.processUrl(sourceUrl),
      type: this.dataConfig.sources[sourceName]?.type || 'geojson',
      options: this.dataConfig.sources[sourceName]?.options || {}
    };
  }
}